import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { fadeUp, stagger } from "../lib/motion";
import { PRICING_PLANS } from "../lib/site";

const cardItem = {
  hidden: { opacity: 0, y: 56 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export const Pricing = () => (
  <section id="pricing" className="relative py-28 lg:py-36 grid-bg border-t border-[#2A2D34]" data-testid="pricing-section">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
        className="mb-16 max-w-3xl"
      >
        <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.3em] text-[#C6FF00] mb-5">
          / Pricing
        </motion.p>
        <motion.h2 variants={fadeUp} className="text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9]">
          Choose Your Growth System
        </motion.h2>
        <motion.p variants={fadeUp} className="mt-6 text-lg text-[#8A939B] font-medium leading-relaxed">
          Every package combines strategy, design and expert guidance so your online presence
          reflects the quality of your brand.
        </motion.p>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5"
      >
        {PRICING_PLANS.map((plan) => (
          <motion.div
            key={plan.name}
            variants={cardItem}
            className={`group relative flex flex-col p-8 lg:p-9 transition-transform duration-300 hover:-translate-y-2 ${
              plan.featured
                ? "border-2 border-[#C6FF00] bg-[#0E1013]"
                : "border border-[#2A2D34] bg-[#0A0C0F] hover:border-[#C6FF00]"
            }`}
            data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {plan.badge && (
              <span className={`absolute -top-3 left-8 text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 ${
                plan.featured ? "bg-[#C6FF00] text-[#0A0C0F]" : "bg-[#1A1D22] text-[#8A939B] border border-[#2A2D34]"
              }`}>
                {plan.badge}
              </span>
            )}
            <h3 className="text-xl font-black uppercase tracking-tight text-white">{plan.name}</h3>
            <div className="mt-5 flex items-end gap-2">
              <span className={`text-5xl font-black tracking-tighter ${plan.featured ? "text-[#C6FF00]" : "text-white"}`}>
                {plan.price}
              </span>
            </div>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{plan.period}</p>

            <div className="my-7 h-px w-full bg-[#2A2D34]" />

            <ul className="flex-1 space-y-3.5">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-[#C7CDD4] font-medium">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#C6FF00]" strokeWidth={3} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <p className="mt-7 text-xs font-bold uppercase tracking-wider text-[#8A939B]">{plan.delivery}</p>

            <Link
              to={`/pricing/${plan.slug}`}
              data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`mt-7 flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-bold uppercase text-xs tracking-wider transition-colors duration-300 ${
                plan.featured
                  ? "btn-shine bg-[#C6FF00] text-[#0A0C0F] hover:bg-white"
                  : "border border-[#2A2D34] text-white hover:border-[#C6FF00] hover:text-[#C6FF00]"
              }`}
            >
              Choose Plan
            </Link>
          </motion.div>
        ))}
      </motion.div>

      <p className="mt-10 text-center text-sm text-[#6B7280] font-medium">
        Once you select a plan and payment clears, onboarding starts automatically — your service agreement to sign, your Site Setup blueprint and your welcome packet, then your portal opens for asset collection, build, approval and launch.
      </p>
    </div>
  </section>
);
